import { ChangeEvent, useCallback, useState } from "react";
import axios from "axios";
import useUiuxStore from "../../store/useUiuxStore";
import ModalWrapper from "../common/modal/ModalWrapper";
import { InputForm } from "../common/form/input/InputForm";
import { findId, issueTempPassword } from "../../apis/authApi";
import { formatPhoneNumber } from "../../utils/string/stringUtil";
import config from "../../config";
import { showError } from 'src/utils/toast/toastUtil';

export const FindAccountModal = () => {
    const { setFindAccountModalVisible } = useUiuxStore();

    const [inputData, setInputData] = useState({
        empNm: "",
        phoneNumber: "",
        passwordUserId: "",
        passwordEmpNm: "",
        passwordPhoneNumber: "",
    });

    const [foundUserId, setFoundUserId] = useState("");
    const [showResultModal, setShowResultModal] = useState(false);
    const [showTempPasswordResultModal, setShowTempPasswordResultModal] = useState(false);

    // 아이디 찾기
    const handleFindIdClick = useCallback(async () => {
        const { empNm, phoneNumber } = inputData;

        if (empNm.trim() === "" || phoneNumber.trim() === "") {
            showError("이름과 휴대폰 번호를 입력해주세요.");
            return;
        }

        const response = await findId(empNm, phoneNumber);
        if (response?.data?.successFlag) {
            setFoundUserId(response.data.data);
            setShowResultModal(true);
        }
        // 실패 시 메시지는 axios 인터셉터에서 서버 response.message 로 표시
    }, [inputData]);


    // 임시 비밀번호 발급
    const handleChangePasswordClick = useCallback(async () => {
        const { passwordUserId, passwordEmpNm, passwordPhoneNumber } = inputData;

        if (passwordUserId.trim() === "") {
            showError("아이디를 입력해주세요.");
            return;
        }
        if (passwordEmpNm.trim() === "") {
            showError("이름을 입력해주세요.");
            return;
        }
        if (passwordPhoneNumber.trim() === "") {
            showError("휴대폰 번호를 입력해주세요.");
            return;
        }

        const response = await issueTempPassword(
            passwordUserId,
            passwordEmpNm,
            passwordPhoneNumber
        );

        if (!response?.data?.successFlag) {
            // 실패 시 메시지는 axios 인터셉터에서 서버 response.message 로 표시
            return;
        }

        const tempPassword = response.data.data;
        const receiver = passwordPhoneNumber.replace(/-/g, "");
        const sender = config.popbill_Sender || "053-283-3700";

        try {
            await axios.post(
                `${config.popbillUrl}/cellPhoneMail/message/short/direct`,
                {
                    corpNum: config.popbill_CorpNum,
                    userID: config.popbill_UserId,
                    listLength: 1,
                    sdate: "",
                    edate: "",
                    cmailArray: [
                        {
                            receiver,
                            receiverName: passwordEmpNm,
                            sender,
                            senderName: "MES",
                            msgContent: `[MES] 임시 비밀번호: ${tempPassword}`,
                            sendType: "short",
                        },
                    ],
                }
            );
            setShowTempPasswordResultModal(true);
        } catch (e) {
            showError("비밀번호는 변경되었으나 문자 발송에 실패했습니다. 관리자에게 문의하세요.");
        }
    }, [inputData, setFindAccountModalVisible]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;

        if (name === "phoneNumber" || name === "passwordPhoneNumber") {
            setInputData((prev) => ({
                ...prev,
                [name]: formatPhoneNumber(value),
            }));
        } else {
            setInputData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    }, []);

    return (
        <>
            <ModalWrapper
                size="sm"
                overlay={true}
                width={800}
                onOverlayClick={() => setFindAccountModalVisible(false)}
                onEscKeyDown={() => setFindAccountModalVisible(false)}
            >
                <div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
                    아이디 / 비밀번호 찾기
                </div>
                <div className="w-full py-[12px] px-[24px] flex flex-col gap-[16px]">
                    <p>아이디 찾기</p>
                    <InputForm
                        title="이름"
                        value={inputData.empNm}
                        editable={true}
                        name="empNm"
                        onChange={handleChange}
                    />
                    <InputForm
                        title="휴대폰 번호"
                        value={inputData.phoneNumber}
                        editable={true}
                        name="phoneNumber"
                        onChange={handleChange}
                    />
                    <button
                        className="w-[140px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold self-end"
                        onClick={handleFindIdClick}
                    >
                        찾기
                    </button>

                    <hr />

                    <p>비밀번호 찾기</p>
                    <InputForm
                        title="아이디"
                        value={inputData.passwordUserId}
                        editable={true}
                        name="passwordUserId"
                        onChange={handleChange}
                    />
                    <InputForm
                        title="이름"
                        value={inputData.passwordEmpNm}
                        editable={true}
                        name="passwordEmpNm"
                        onChange={handleChange}
                    />
                    <InputForm
                        title="휴대폰 번호"
                        value={inputData.passwordPhoneNumber}
                        editable={true}
                        name="passwordPhoneNumber"
                        onChange={handleChange}
                    />
                    <button
                        className="w-[140px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold self-end"
                        onClick={handleChangePasswordClick}
                    >
                        임시 비밀번호 발급
                    </button>
                </div>
                <div className="w-full flex justify-end items-center gap-[8px] px-[24px] py-[12px]">
                    <button
                        className="w-[140px] text-white rounded-[4px] bg-red-500 px-[16px] font-semibold"
                        onClick={() => setFindAccountModalVisible(false)}
                    >
                        닫기
                    </button>
                </div>
            </ModalWrapper>

            {showResultModal && (
                <ModalWrapper
                    size="qxs"
                    overlay={true}
                    width={400}
                    onOverlayClick={() => setShowResultModal(false)}
                    onEscKeyDown={() => setShowResultModal(false)}
                >
                    <div className="w-full flex items-center px-[24px] py-[16px] bg-gray-100">
                        아이디 찾기 결과
                    </div>
                    <div className="w-full px-[24px] py-[24px] text-center">
                        <p>회원님의 아이디는</p>
                        <p className="text-xl font-bold my-[8px]">"{foundUserId}"</p>
                        <p>입니다.</p>
                    </div>
                    <div className="w-full flex justify-end gap-[8px] px-[24px] py-[12px]">
                        <button
                            className="w-[100px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold"
                            onClick={() => setShowResultModal(false)}
                        >
                            확인
                        </button>
                    </div>
                </ModalWrapper>
            )}

            {showTempPasswordResultModal && (
                <ModalWrapper
                    size="qxs"
                    overlay={true}
                    width={400}
                    onOverlayClick={() => setShowTempPasswordResultModal(false)}
                    onEscKeyDown={() => setShowTempPasswordResultModal(false)}
                >
                    <div className="w-full flex items-center px-[24px] py-[16px] bg-gray-100">
                        임시 비밀번호 발급 결과
                    </div>
                    <div className="w-full px-[24px] py-[24px] text-center min-h-[70px] flex items-center justify-center">
                        <p>임시 비밀번호가 문자로 발송되었습니다.</p>
                    </div>
                    <div className="w-full flex justify-end gap-[8px] px-[24px] py-[12px]">
                        <button
                            className="w-[100px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold"
                            onClick={() => {
                                setShowTempPasswordResultModal(false);
                                setFindAccountModalVisible(false);
                            }}
                        >
                            확인
                        </button>
                    </div>
                </ModalWrapper>
            )}
        </>
    );
};